// 261005 E2-P2a — khung Trợ lý AI (vỏ + tải dữ liệu + ô soạn). ⑤ (một tập có kịch bản) + ② Khung xương (cả truyện, E3-P1b).
// Mở khung = 0 AI (chỉ GET). AI chỉ chạy khi người dùng bấm Gửi / "3 phương án" / nút hành động có nhãn chi phí.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Dropdown, Input, Modal, Spin, message } from 'antd';
import Button from '../WbButton';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { adaptationAssistantApi } from '../../../../services/api/adaptationAssistant';
import type { AssistantAction, AssistantBlock, AssistantInteraction, AssistantUiStage, AssistantVersion, InteractionMode } from '../../../../types/adaptationAssistant';
import { resolveSkeletonIssueIds } from './skeletonBlockView';
import type { WorkbenchStageKey } from '../../../../types/adaptationWorkbench';
import { formatShortDateTime, humanizeCodes, recoverRequest } from '../../adaptationUtils';
import { Dot, Pill } from '../WbSlot';
import { MAX_FIX_TEXT, chipSceneLabel, toggleSelected } from '../../episodeFixUtils';
import { useWorkbenchCtx } from '../WorkbenchContext';
import AssistantTimeline from './AssistantTimeline';
import { applyAssistantIntent, autoSendDecision, fixDraftOf, resolvedIssueCount, shouldApplyIntent } from './assistantIntent';
import AssistantVersions from './AssistantVersions';
import RelinkCard from './RelinkCard';
import {
  appliedMessage, assistantActionErrorText, assistantErrorText, assistantOverviewBlock, type AssistantOverviewBlock, assistantScopeOf, chipIdsOf, composerState, goneText, hasLiveRunning, historyMenuView, retryDraftOf, newRequestId, previewFollowUp, restoreBlockerText, restoredMessage, scopeTexts, scriptChipsOf,
  groupChips, shouldReloadAfterError, skeletonChipLabel, skeletonChipsOf, sourceChipLabel, usesViolationChips, localizeChipLabel, localizeChipsOf, handoffWarningOf,
  autoApplyToastText, undoConfirmText,
} from './assistantView';
import { useAssistantScope } from './useAssistantScope';


interface Draft { text: string; selected: string[] }
const NO_DRAFT: Draft = { text: '', selected: [] };

const EDITING_WHY = 'Đang sửa tay — Lưu hoặc Huỷ trước';

// F24: để NGOÀI component — khai báo trong thân hàm làm mỗi lần render là một kiểu mới ⇒ svg trong nút bị dựng lại, nuốt cú bấm đầu.
const Icon: React.FC<{ d: string }> = ({ d }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);

interface Props { stage: WorkbenchStageKey; onCollapse: () => void }

const AssistantPanel: React.FC<Props> = ({ stage, onCollapse }) => {
  const ctx = useWorkbenchCtx();
  const { storyId, dataVersion, data, busy: ctxBusy, refreshAll, goTo, scriptJob, scriptEpisode, scriptEpisodeNoScript, assistantIntent, clearAssistantIntent, scriptEditing, scriptSaveNonce } = ctx;
  const scope = assistantScopeOf(stage, scriptEpisode, scriptEpisodeNoScript);
  const a = useAssistantScope(storyId, scope, () => { refreshAll(); }, scriptSaveNonce, dataVersion);
  const scopeKey = scope.supported ? scope.scopeKey : '';
  const episode = scope.supported ? (scope.episode ?? 0) : 0;
  const stageKey: AssistantUiStage = scope.supported ? scope.stageKey : 'script';
  const isScript = stageKey === 'script';
  const violationChips = usesViolationChips(stageKey);   // ② ④: chip kiểu vi phạm (khác chip lỗi tập của ⑤)
  // E4-P1: đang sửa tay đúng tập này ⇒ mọi nút GHI tắt kèm lý do (gửi chat và Bỏ vẫn được, như luật `ctx.busy`). Chỉ ⑤.
  const editingHere = isScript && scriptEditing !== null && scriptEditing === episode;
  const busy = ctxBusy || editingHere;
  const busyReason = editingHere ? EDITING_WHY : null;

  const [drafts, setDrafts] = useState<Record<string, Draft>>({}); // theo scopeKey — mất khi Thu gọn (Y4 chấp nhận)
  const draft = drafts[scopeKey] ?? NO_DRAFT;
  const setDraft = (patch: Partial<Draft>) => setDrafts((d) => ({ ...d, [scopeKey]: { ...(d[scopeKey] ?? NO_DRAFT), ...patch } }));
  const [sending, setSending] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);   // F24: menu lịch sử điều khiển bằng state — chọn xong tự đóng
  const [acting, setActing] = useState<string | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const pending = useRef<{ requestId: string; sig: string } | null>(null);
  const handledNonce = useRef<number | null>(null);
  const autoSentNonce = useRef<number | null>(null);   // E3-P4b1: "Đưa ra phương án" / P5a "Nhờ AI sửa" tự gửi đúng MỘT lần mỗi nonce
  const fixSent = useRef<{ ids: string[]; at: number } | null>(null);   // 261015 P5a (D5): khối của lượt `fix` vừa gửi — toast "Đã tự áp" khi thread tải lại có kết quả
  const onUndoRef = useRef<(b: AssistantBlock) => unknown>(() => undefined);
  const draftsRef = useRef(drafts); draftsRef.current = drafts;
  const textRef = useRef<TextAreaRef>(null);

  const script = useMemo(() => data.scripts.find((s) => s.episode === episode), [data.scripts, episode]);
  const nextScript = useMemo(() => data.scripts.find((s) => s.episode === episode + 1) ?? null, [data.scripts, episode]);
  const scenes = useMemo(
    () => (script?.script?.scenes ?? []).map((s, i) => ({ scenePlanId: String(s.scenePlanId ?? ''), index: i + 1 })).filter((s) => s.scenePlanId),
    [script],
  );

  // ② : tên nhân vật/bối cảnh cho dòng "ảnh hưởng" (tra từ khung xương đang tải; id lạ ⇒ chính id).
  const outlineNames = useMemo(() => {
    const m = new Map<string, string>();
    const o = data.record?.outline as { characters?: Array<{ id: string; name: string }>; locations?: Array<{ id: string; name: string }> } | null | undefined;
    for (const c of o?.characters ?? []) m.set(c.id, c.name);
    for (const l of o?.locations ?? []) m.set(l.id, l.name);
    return m;
  }, [data.record]);
  const nameOf = useCallback((id: string) => outlineNames.get(id) ?? id, [outlineNames]);

  const view = a.view;
  // F19: lượt RUNNING quá hạn (BE khởi động lại giữa chừng) KHÔNG khoá ô gửi vô hạn; đồng hồ chỉ chạy khi có lượt RUNNING.
  const [now, setNow] = useState(() => Date.now());
  const anyRunning = !!a.detail?.interactions.some((it) => it.status === 'RUNNING');
  useEffect(() => {
    if (!anyRunning) return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, [anyRunning]);
  const runningInteraction = hasLiveRunning(a.detail, now);
  const composer = composerState({ view, sending, runningInteraction, text: draft.text, selected: draft.selected, stageKey });
  const locked = busy || !!acting;

  // 261015 P4c-2c-1 (D10): OVERVIEW_NOT_EFFECTIVE / OVERVIEW_CHANGED ở ⑤ ⇒ ngoài toast còn khối có nút "Mở Tổng quan truyện" (khuôn ④ SplitStage). Đổi tập / chặng ⇒ bỏ khối.
  const [overviewBlock, setOverviewBlock] = useState<AssistantOverviewBlock | null>(null);
  useEffect(() => { setOverviewBlock(null); }, [storyId, scopeKey]);

  const fail = useCallback(async (e: unknown, registryId?: string) => {
    const ob = assistantOverviewBlock(e, stageKey);
    if (ob) setOverviewBlock(ob);
    message.error(registryId ? assistantActionErrorText(e, registryId, stageKey) : assistantErrorText(e, stageKey), 8);
    if (shouldReloadAfterError(e)) await a.reloadAll();
  }, [a, stageKey]);

  /** `registryId` (nút hành động của Trợ lý) ⇒ lỗi dùng câu riêng của hành động đó (vd `approve_skeleton` ⇒ "hỏi lại trợ lý"). */
  const run = async (key: string, fn: () => Promise<void>, okMsg?: string, registryId?: string) => {
    setActing(key);
    try { await fn(); if (okMsg) message.success(okMsg); } catch (e) { await fail(e, registryId); } finally { setActing(null); }
  };

  const afterWrite = async () => { await refreshAll(); await a.reloadAll(); };

  // ── gửi ────────────────────────────────────────────────────────────────────────
  // `draftOverride` (N5): tự gửi từ intent truyền thẳng nháp vừa dựng — state React chưa kịp cập nhật trong effect, và `composer` đã cũ.
  const doSend = async (mode: InteractionMode, draftOverride?: Draft) => {
    const d = draftOverride ?? draft;
    const enabled = draftOverride ? composerState({ view, sending, runningInteraction, text: d.text, selected: d.selected, stageKey }).enabled : composer.enabled;
    if (!enabled || !scope.supported) return;
    setSending(true);
    const body = { text: d.text.trim(), mode, issueIds: d.selected };
    const sig = JSON.stringify(body);
    // Cùng nội dung bấm lại sau lỗi mạng ⇒ dùng lại requestId (server trả lượt cũ, không gọi AI lần 2).
    const requestId = pending.current?.sig === sig ? pending.current.requestId : newRequestId();
    pending.current = { requestId, sig };
    try {
      const tid = await a.ensureThread();
      const sent = await adaptationAssistantApi.send(tid, { requestId, ...body });
      pending.current = null;
      setDraft({ text: '', selected: [] });
      if (mode === 'fix') {
        // 261015 P5a: lượt `fix` có thể TỰ ÁP ngay trong request ⇒ khung xương / chia tập / kịch bản đã ghi — tải lại dữ liệu chung + `head.modifiedAt`
        // (không thì "Khôi phục bản trước" tắt vì "đã đổi sau khi áp" và chặng phía sau cũ). Lỗi tải lại KHÔNG phải lỗi gửi (AI đã trả lời, đã tốn tiền).
        const ids = (sent?.blocks ?? []).map((b) => b.id);
        if (ids.length) fixSent.current = { ids, at: Date.now() };
        try { await afterWrite(); } catch { await a.reloadThread(tid); }
      } else {
        await a.reloadThread(tid);
      }
    } catch (e) {
      if ((e as { response?: unknown })?.response) pending.current = null; // server đã trả lời (lỗi nghiệp vụ) ⇒ lần sau là lượt mới
      await fail(e);
    } finally { setSending(false); }
  };

  // F19 "Gửi lại": đưa chữ + lỗi đã tick của lượt hỏng về ô soạn (không tự gửi — gửi là tốn AI). Ô đang có chữ ⇒ không đè.
  const onRetry = (it: AssistantInteraction) => {
    if (draft.text.trim() || draft.selected.length) { message.info('Ô soạn đang có nội dung — gửi hoặc xoá nó trước.', 4); return; }
    setDraft(retryDraftOf(it));
    setTimeout(() => textRef.current?.focus(), 0);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const native = e.nativeEvent as KeyboardEvent & { isComposing?: boolean };
    if (native.isComposing || e.keyCode === 229) return; // đang gõ IME
    e.preventDefault();
    doSend('free');
  };

  // ── khối / hành động ───────────────────────────────────────────────────────────
  const onPreview = (b: AssistantBlock) => run(`preview-${b.id}`, async () => {
    const r = await adaptationAssistantApi.previewBlock(b.id);
    // Có job nền (⑤ · ④ viết lại) ⇒ tải lại để dải job + khoá hiện ngay; xem thử đồng bộ (② · ④ lệnh cắt) thì không có job.
    const f = previewFollowUp(stageKey, r);
    if (f.refresh) await refreshAll();
    await a.reloadThread();
    if (f.message) message.success(f.message);
  });

  const reportRelink = (r: { versionNo?: number; relink?: { state: string; nextEpisode: number } | null }, verb: string) => {
    const v = r.versionNo !== undefined ? ` (v${r.versionNo})` : '';
    if (r.relink?.state === 'RUNNING') message.info(`${verb}${v}. Đoạn kết đổi — AI đang kiểm nối với tập ${r.relink.nextEpisode}.`, 6);
    else if (r.relink?.state === 'RELINKED') message.success(`${verb}${v} và nối lại tập ${r.relink.nextEpisode}.`, 6);
    else message.success(`${verb}${v}.`);
  };

  const onApply = (b: AssistantBlock) => run(`apply-${b.id}`, async () => {
    const r = await adaptationAssistantApi.applyBlock(b.id);
    await afterWrite();
    const msg = appliedMessage(stageKey, r, 'apply');
    if (msg) message.success(msg, 6); else reportRelink(r, 'Đã áp');
    const hw = handoffWarningOf(stageKey, r); if (hw) message.warning(hw, 10);
  });

  // E2-P3: hoàn tác MỘT khối / áp lại (0 AI). Lệch (SCRIPT_CHANGED / CONTENT_DIVERGED / VERSION_PRUNED) ⇒ `fail` báo câu riêng + tải lại luồng.
  const onUndo = (b: AssistantBlock) => run(`undo-${b.id}`, async () => {
    const r = await adaptationAssistantApi.undoBlock(b.id);
    await afterWrite();
    message.success(`Đã khôi phục bản trước${r.appliedResult.restoreVersionNo !== undefined ? ` (v${r.appliedResult.restoreVersionNo})` : ''}. Bản vừa gỡ vẫn còn ở Phiên bản.`, 6);
    const hw = handoffWarningOf(stageKey, r.appliedResult); if (hw) message.warning(hw.replace('Đã áp', 'Đã khôi phục'), 10);
  });

  onUndoRef.current = onUndo;
  /** Khôi phục từ toast: vẫn qua hộp xác nhận (② mất dấu chốt) như Popconfirm của thẻ. */
  const confirmUndo = (b: AssistantBlock) => {
    const t = undoConfirmText(stageKey);
    Modal.confirm({ title: t.title, content: t.content, okText: 'Khôi phục', cancelText: 'Thôi', onOk: () => onUndoRef.current(b) });
  };
  // 261015 P5a (D5): toast 8 giây "Đã tự áp …" + nút Khôi phục — đọc `diff.autoApply` từ thread vừa tải (kết quả POST chỉ có `blockBrief`, không có diff).
  useEffect(() => {
    const sent = fixSent.current;
    if (!sent || !a.detail) return;
    if (Date.now() - sent.at > 60000) { fixSent.current = null; return; }
    const blocks = a.detail.interactions.flatMap((it) => it.blocks).filter((b) => sent.ids.includes(b.id));
    if (!blocks.length) return;   // thread chưa là bản sau lượt — đợi lần tải kế
    fixSent.current = null;
    for (const b of blocks) {
      const text = autoApplyToastText(b);
      if (!text) continue;
      const key = `auto-apply-${b.id}`;
      message.success({
        key, duration: 8,
        content: (<span>{text}{' '}<Button type="link" size="small" style={{ padding: 0 }} onClick={() => { message.destroy(key); confirmUndo(b); }}>Khôi phục bản trước</Button></span>),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.detail]);

  const onReapply = (b: AssistantBlock) => run(`reapply-${b.id}`, async () => {
    const r = await adaptationAssistantApi.reapplyBlock(b.id);
    await afterWrite();
    const msg = appliedMessage(stageKey, r.appliedResult, 'reapply');
    if (msg) message.success(msg, 6); else reportRelink(r.appliedResult, 'Đã áp lại');
    const hw = handoffWarningOf(stageKey, r.appliedResult); if (hw) message.warning(hw, 10);
  });

  const onDismiss = (b: AssistantBlock) => run(`dismiss-${b.id}`, async () => {
    await adaptationAssistantApi.dismissBlock(b.id);
    await a.reloadThread();
  });

  const onExecute = (act: AssistantAction) => run(`action-${act.id}`, async () => {
    await adaptationAssistantApi.executeAction(act.id, act.cost === 'free' ? {} : { confirm: true });
    if (act.registryId.startsWith('go_stage:')) { goTo({ stage: act.registryId.slice('go_stage:'.length) as WorkbenchStageKey }); return; }
    if (act.registryId === 'open_versions') { setVersionsOpen(true); await a.loadVersions(); }
    else if (act.registryId === 'rewrite_episode' || act.registryId === 'run_season_reader' || act.registryId === 'approve_skeleton' || act.registryId === 'repair_episodes' || act.registryId === 'scan_story') await refreshAll(); // job/ghi do server chạy ⇒ dải job + khoá hiện ngay
    await a.reloadThread();
  }, undefined, act.registryId);

  // ── nối tập / phiên bản ────────────────────────────────────────────────────────
  const relink = view?.relink ?? null;
  const onRelink = (reason?: string) => run('relink', async () => {
    if (!relink || !nextScript?.modifiedAt) throw new Error('Thiếu modifiedAt của tập sau — tải lại danh sách rồi thử lại');
    await adaptationAssistantApi.relink(relink.id, { expectedModifiedAt: nextScript.modifiedAt, ...(reason ? { reason } : {}) });
    await afterWrite();
    message.success(`Đã nối lại tập ${relink.nextEpisode}. Nhớ cho AI đọc lại cả mùa trước khi handoff.`, 6);
  });
  // 261015 P4a (D9) — Hoàn tác tự nối: gọi, làm mới thẻ + danh sách kịch bản (cùng `afterWrite` của nối tay); lỗi hiện qua `run` (câu theo mã, mã "đã đổi" ⇒ tải lại luồng).
  const onUndoAuto = () => run('undo-auto', async () => {
    if (!relink || !nextScript?.modifiedAt) throw new Error('Thiếu modifiedAt của tập sau — tải lại danh sách rồi thử lại');
    await adaptationAssistantApi.undoAutoRelink(relink.id, { expectedModifiedAt: nextScript.modifiedAt });
    await afterWrite();
    message.success(`Đã hoàn tác tự nối tập ${relink.nextEpisode} — thẻ trở lại để bạn nối tay.`, 6);
  });
  const onRewrite = (from: number) => {
    const req = recoverRequest(data.scripts, from);
    if (req) scriptJob.writeScripts(req);
  };

  const onRestore = (v: AssistantVersion) => run(`restore-${v.versionId}`, async () => {
    if (!view || !scope.supported) return;
    const r = await adaptationAssistantApi.restoreVersion(v.versionId, { storyId, stageKey: scope.stageKey, scopeKey: scope.scopeKey, expectedModifiedAt: view.head.modifiedAt });
    await afterWrite();
    const msg = restoredMessage(stageKey, v.versionNo, r.versionNo);
    if (msg) message.success(msg, 6); else reportRelink(r, `Đã khôi phục v${v.versionNo} (thành v${r.versionNo})`);
    const hw = handoffWarningOf(stageKey, r); if (hw) message.warning(hw.replace('Đã áp', 'Đã khôi phục'), 10);
  });

  const onVersionsOpen = (open: boolean) => { setVersionsOpen(open); if (open && a.versions === null) a.loadVersions(); };

  // 261005 E2-P2b — xử lý ý định từ ⑤ (đặt TRƯỚC `return` sớm — rule of hooks). Đúng MỘT lần mỗi nonce (StrictMode chạy effect 2 lần).
  // Chỉ áp khi scope + view đã là của đúng tập (view tập cũ còn một nhịp — `shouldApplyIntent` so `view.head.episode`). Không tự gửi.
  const scopeSupported = scope.supported;
  useEffect(() => {
    if (!assistantIntent) return;
    const verdict = shouldApplyIntent(assistantIntent, handledNonce.current, scope, a.view, a.loading);
    if (verdict === 'wait') return;
    handledNonce.current = assistantIntent.nonce;
    if (verdict === 'apply') {
      // ②: tra id chip của BE theo vi phạm FE gửi lên (FE không tự tính id); không khớp ⇒ `applyAssistantIntent` điền câu hỏi thay vì tick.
      const resolved = assistantIntent.issueRefs?.length ? resolveSkeletonIssueIds(skeletonChipsOf(a.view), assistantIntent.issueRefs) : [];
      const intent = resolved.length || assistantIntent.issueRefs?.length ? { ...assistantIntent, issueIds: [...assistantIntent.issueIds, ...resolved] } : assistantIntent;
      const draftBefore = draftsRef.current[scopeKey] ?? NO_DRAFT;
      const r = applyAssistantIntent(draftBefore, intent, chipIdsOf(a.view));
      setDrafts((d) => ({ ...d, [scopeKey]: r.draft }));
      if (assistantIntent.openVersions) onVersionsOpen(true);
      // E3-P4b1: "Đưa ra phương án" ⇒ tự gửi 3 phương án đúng một lần (nháp đang có chữ / composer tắt ⇒ không gửi, nói lý do).
      // 261015 P5a (D1): "Nhờ AI sửa (tốn AI)" ⇒ tự gửi `mode:'fix'` + FIX_REQUEST_TEXT; ô soạn có chữ / lỗi đã tick / chip đã mất ⇒ quay về hành vi cũ (điền + báo).
      let notice = r.notice;
      if (assistantIntent.autoSend) {
        const isFix = assistantIntent.autoSend === 'fix';
        const gate = composerState({ view: a.view, sending, runningInteraction, text: 'x', selected: [], stageKey });
        const dec = autoSendDecision({
          intent: assistantIntent, verdict, sentNonce: autoSentNonce.current, draftHadText: draftBefore.text.trim() !== '', blockedReason: gate.enabled ? null : gate.reason,
          draftHadChips: draftBefore.selected.length > 0, resolvedCount: resolvedIssueCount(intent, chipIdsOf(a.view)),
        });
        if (dec.notice) notice = dec.notice;
        const sendDraft = isFix ? fixDraftOf(r.draft) : r.draft;
        if (dec.send && (sendDraft.text.trim() || sendDraft.selected.length)) { autoSentNonce.current = assistantIntent.nonce; void doSend(isFix ? 'fix' : 'options', sendDraft); }
      }
      if (notice) message.info(notice, 6);
      if (!a.view?.editBlocker) setTimeout(() => textRef.current?.focus(), 0); // ô soạn ẩn (tập không nháp) ⇒ bỏ qua focus
    }
    clearAssistantIntent(assistantIntent.nonce);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantIntent, scopeSupported, scopeKey, a.view, a.loading]);

  // ── vẽ ─────────────────────────────────────────────────────────────────────────
  const scopeLabel = scope.supported ? scopeTexts(scope).unit : '';
  const threadsForMenu = a.threads;
  const hist = historyMenuView(threadsForMenu.length, a.loading);
  const header = (
    <div className="wb-ai-head">
      <span className="wb-ai-title"><span className="wb-ai-logo" aria-hidden="true">✦</span>Trợ lý{scopeLabel ? <Pill tone="ind">{scopeLabel}</Pill> : null}</span>
      <div className="wb-ai-tools">
        {scope.supported && !a.gone && (
          <>
            <Dropdown
              trigger={['click']} open={historyOpen && !hist.buttonDisabled} onOpenChange={(o) => setHistoryOpen(o)}
              menu={{
                selectedKeys: a.threadId ? [a.threadId] : [],
                items: hist.showLoadingItem ? [{ key: '__loading', label: 'Đang tải…', disabled: true }] : threadsForMenu.map((t, i) => ({ key: t.id, label: `${i === 0 ? 'Mới nhất' : `Cuộc ${threadsForMenu.length - i}`} · ${formatShortDateTime(t.createdAt)}` })),
                onClick: ({ key }) => { setHistoryOpen(false); a.selectThread(key); },
              }}
            >
              <Button type="text" aria-label="Lịch sử cuộc trò chuyện" title="Lịch sử" disabled={hist.buttonDisabled}><Icon d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5M12 7v5l3 2" /></Button>
            </Dropdown>
            <Button type="text" aria-label="Cuộc mới" title="Cuộc mới" disabled={sending || (!a.threadId && !(a.detail?.interactions.length))} onClick={() => a.newConversation()}>
              <Icon d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
            </Button>
          </>
        )}
        <Button type="text" onClick={onCollapse} aria-label="Đóng khung Trợ lý AI" title="Đóng Trợ lý (nút «✦ Trợ lý» ở thanh trên mở lại)">Đóng ✕</Button>
      </div>
    </div>
  );

  if (!scope.supported || a.gone) {
    return (
      <section className="wb-ai" aria-label="Trợ lý AI">
        {header}
        <div className="wb-ai-scroll">
          <span className="wb-ai-note">{scope.supported ? goneText(stageKey, episode) : scope.reason}</span>
        </div>
      </section>
    );
  }

  const texts = scopeTexts(scope);
  const interactions = a.detail?.interactions ?? [];
  const hasContent = interactions.length > 0;

  // Chip lỗi (nhãn người đọc — không in mã máy): đã tick nằm TRONG ô soạn, chưa tick mở bằng "+ Gắn lỗi".
  type ChipItem = { id: string; label: string; bad: boolean };
  const chipItems: ChipItem[] = !view ? [] : violationChips
    ? skeletonChipsOf(view).map((c) => ({
      id: c.id, bad: c.severity === 'ERROR',
      label: humanizeCodes(
        stageKey === 'source' ? sourceChipLabel(c)
          : stageKey === 'localize' ? localizeChipLabel(localizeChipsOf(view).find((x) => x.id === c.id) ?? { ...c, kind: 'violation' as const })
            : skeletonChipLabel(c),
        nameOf,
      ).replace(/^⛔\s*/, ''),
    }))
    : isScript
      ? scriptChipsOf(view).map((c) => ({
        id: c.id, bad: !!c.blocking,
        label: humanizeCodes(`${c.text} · ${chipSceneLabel(c, scenes)}${c.otherEpisodes.length ? ` · còn tập ${c.otherEpisodes.join(', ')}` : ''}`, nameOf),
      }))
      : [];
  const pickedChips = chipItems.filter((c) => draft.selected.includes(c.id));
  const freeChips = chipItems.filter((c) => !draft.selected.includes(c.id));

  return (
    <section className="wb-ai" aria-label="Trợ lý AI">
      {header}

      {editingHere && <Alert type="warning" showIcon style={{ margin: '8px 16px 0' }} message={EDITING_WHY} />}
      {overviewBlock && (
        <Alert
          style={{ margin: '8px 16px 0' }} type="warning" showIcon closable onClose={() => setOverviewBlock(null)} message={overviewBlock.text}
          description={overviewBlock.extra.length ? overviewBlock.extra.map((x, i) => <div key={i} style={{ fontSize: 13 }}>• {x}</div>) : undefined}
          action={<Button size="small" type="primary" onClick={() => { setOverviewBlock(null); goTo({ stage: 'skeleton', intent: 'openOverview' }); }}>Mở Tổng quan truyện</Button>}
        />
      )}
      {a.loadError ? <Alert type="error" showIcon style={{ margin: '8px 16px 0' }} message={assistantErrorText(a.loadError)} action={<Button size="small" onClick={() => a.reloadAll()}>Tải lại</Button>} /> : null}

      {relink && (
        <RelinkCard
          card={relink} episode={episode} editBlocker={view?.editBlocker ?? null} nextScript={nextScript}
          busy={busy} busyReason={busyReason} acting={acting === 'relink' || acting === 'undo-auto'} onRelink={onRelink} onRewrite={onRewrite}
          onUndoAuto={onUndoAuto} undoing={acting === 'undo-auto'}
        />
      )}

      <div className="wb-ai-scroll">
        {a.loading && !view && <Spin size="small" />}
        {!hasContent && !a.loading && (
          <div>
            <p className="wb-ai-bot">{texts.intro}</p>
            <span className="wb-ai-note">AI chỉ chạy khi bạn gửi tin hoặc bấm nút.</span>
          </div>
        )}
        <AssistantTimeline
          interactions={interactions} nextEpisode={episode + 1} headModifiedAt={view?.head.modifiedAt ?? null} busy={busy} busyReason={busyReason} acting={acting} nameOf={nameOf} chipLabels={Object.fromEntries(chipItems.map((c) => [c.id, c.label]))}
          onPreview={onPreview} onApply={onApply} onUndo={onUndo} onReapply={onReapply} onDismiss={onDismiss} onExecute={onExecute}
          now={now} onRetry={onRetry}
        />
        <AssistantVersions
          open={versionsOpen} onOpenChange={onVersionsOpen} versions={a.versions} stageKey={stageKey}
          restoreBlocker={restoreBlockerText(view?.editBlocker, locked, busyReason)} acting={acting} onRestore={onRestore}
        />
      </div>

      {!composer.show && composer.reason && <Alert type="info" showIcon style={{ margin: '0 16px 8px' }} message={composer.reason} />}
      {composer.show && (
        <div className="wb-compose-wrap">
          <div className="wb-compose">
            {pickedChips.length > 0 && (
              <div className="wb-chips">
                {groupChips(pickedChips).map((g) => (
                  <span key={g.ids.join('|')} className="wb-chip" title={g.title}>
                    <Dot tone={g.bad ? 'bad' : 'warn'} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{g.label}</span>
                    <button type="button" className="wb-chip-x" aria-label={`Bỏ gắn: ${g.label}`} onClick={() => setDraft({ selected: draft.selected.filter((x) => !g.ids.includes(x)) })}>×</button>
                  </span>
                ))}
              </div>
            )}
            <Input.TextArea
              ref={textRef} autoSize={{ minRows: 2, maxRows: 7 }} value={draft.text} maxLength={MAX_FIX_TEXT} onChange={(e) => setDraft({ text: e.target.value })} onKeyDown={onKeyDown}
              placeholder={texts.placeholder} aria-label="Tin nhắn cho trợ lý"
            />
            <div className="wb-compose-row">
              {chipItems.length > 0 ? (
                <Dropdown
                  trigger={['click']} disabled={freeChips.length === 0}
                  menu={{
                    className: 'wb-chip-menu',
                    items: freeChips.map((c) => ({ key: c.id, label: <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{c.label}</span> })),
                    onClick: ({ key }) => setDraft({ selected: toggleSelected(draft.selected, key) }),
                  }}
                >
                  <Button type="text" size="small" disabled={freeChips.length === 0} title={texts.chipsLabel}>+ Gắn lỗi</Button>
                </Dropdown>
              ) : <span />}
              <div className="wb-compose-right">
                <Button className="wb-opts-btn" size="small" disabled={!composer.enabled} onClick={() => doSend('options')}>✦ 3 phương án</Button>
                <Button className="wb-send" type="primary" aria-label="Gửi" loading={sending} disabled={!composer.enabled} onClick={() => doSend('free')}>
                  {!sending && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5M6 11l6-6 6 6" /></svg>}
                </Button>
              </div>
            </div>
          </div>
          <span className="wb-compose-hint">{!composer.enabled && composer.reason ? composer.reason : 'AI chỉ chạy khi bạn gửi · mọi thay đổi đều khôi phục được'}</span>
        </div>
      )}
    </section>
  );
};

export default AssistantPanel;
